import Icon, { COLORS, ICONS } from '../../general-ui/icon';
import { useState, useMemo, useEffect } from 'react';
import RegisteredUserRow from '../people/registered-user';
import { BrandliveEvent } from '../../../types/working-model';
import { getEventRegistrations, searchEventRegistrations } from "../../../store/actions/admin/people";
import { useDispatch } from 'react-redux';
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import WaitingIndicator from "../../general-ui/waiting-indicator/waiting-indicator";
import Paging from "../../general-ui/paging/paging";

interface Props {
	selectedEvent: BrandliveEvent;
}

enum REGISTERED_USER_ORDER_OPTIONS {
	NAME_ASC = 1,
	NAME_DESC,
	EMAIL_ASC,
	EMAIL_DESC,
	LOCATION_ASC,
	LOCATION_DESC,
	DATE_ASC,
	DATE_DESC
}

export default function RegistrationsList(props: Props): JSX.Element {

	const { selectedEvent } = props;

	const {
		users,
		loadingUsers,
		count,
		loadingCount,
		token,
			user,
		searchValue
	} = useTypedSelector(state => ({
		users: state.RegisteredUsersReducer.eventUsers,
		loadingUsers: state.RegisteredUsersReducer.loadingUsers,
		count: state.RegisteredUsersReducer.eventUsersCount,
		loadingCount: state.RegisteredUsersReducer.loadingCount,
		token: state.AuthReducer.token,
		user: state.AuthReducer.user,
		searchValue: state.RegisteredUsersReducer.eventSearchValue
	}));

	const [listPage, setListPage] = useState(1);
	const [maxUserPage, setMaxUserPage] = useState(1);
	const [order, setOrder] = useState(REGISTERED_USER_ORDER_OPTIONS.DATE_DESC);

	const dispatch = useDispatch();


	const registeredUsers = useMemo(() => {
		if (users) {
			return users.map((user: any, index) => {
				return <RegisteredUserRow profile={user} key={String(user.registration_id) + String(index)} />;
			});
		} else { return []; }
	}, [users]);

	useEffect(() => {
		setMaxUserPage(Math.ceil(count / 50));
	}, [count]);


	function handleArrowClick(arrowClicked: string): void {

		setListPage(1);

		switch (arrowClicked) {
			case 'NAME': {
				order === REGISTERED_USER_ORDER_OPTIONS.NAME_ASC ? setOrder(REGISTERED_USER_ORDER_OPTIONS.NAME_DESC) : setOrder(REGISTERED_USER_ORDER_OPTIONS.NAME_ASC);
				break;
			}
			case 'EMAIL': {
				order === REGISTERED_USER_ORDER_OPTIONS.EMAIL_ASC ? setOrder(REGISTERED_USER_ORDER_OPTIONS.EMAIL_DESC) : setOrder(REGISTERED_USER_ORDER_OPTIONS.EMAIL_ASC);
				break;
			}
			case 'LOCATION': {
				order === REGISTERED_USER_ORDER_OPTIONS.LOCATION_ASC ? setOrder(REGISTERED_USER_ORDER_OPTIONS.LOCATION_DESC) : setOrder(REGISTERED_USER_ORDER_OPTIONS.LOCATION_ASC);
				break;
			}
			case 'DATE': {
				order === REGISTERED_USER_ORDER_OPTIONS.DATE_ASC ? setOrder(REGISTERED_USER_ORDER_OPTIONS.DATE_DESC) : setOrder(REGISTERED_USER_ORDER_OPTIONS.DATE_ASC);
				break;
			}
		}

	}

	//If the user navigates to another page through pagination, don't send back to page 1. 
	useEffect(() => {
		if (user) {
			if (searchValue) {
				dispatch(searchEventRegistrations(token!, user.active_channel, searchValue, (listPage - 1), order, Number(selectedEvent.event)));
			} else {
				dispatch(getEventRegistrations(token!, user!.active_channel, Number(selectedEvent.event), (listPage - 1), order));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [listPage]);

	//If a new search value has been entered or a new order has been selected, send back to page 1. 
	useEffect(() => {
		if (user) {
			if (searchValue) {
				dispatch(searchEventRegistrations(token!, user.active_channel, searchValue, (listPage - 1), order, Number(selectedEvent.event)));
			} else {
				dispatch(getEventRegistrations(token!, user!.active_channel, Number(selectedEvent.event), (listPage - 1), order));
			}
			setListPage(1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue, order]);


	return (
		<div className="registrations-list-container">
			<ul>
				<li className={'registered-users-list-item'}>
					<div className={'header'}>
						<span>NAME</span>
						<span
							className={'header-arrows'}
							onClick={() => (handleArrowClick('NAME'))}
							style={{ cursor: "pointer" }}>
							<Icon
								name={ICONS.LITTLE_ARROW_UP}
								size={10}
								color={order === REGISTERED_USER_ORDER_OPTIONS.NAME_ASC ? COLORS.CYAN : COLORS.WHITE}
							/>
							<Icon
								name={ICONS.LITTLE_ARROW_DOWN}
								size={10}
								color={order === REGISTERED_USER_ORDER_OPTIONS.NAME_DESC ? COLORS.CYAN : COLORS.WHITE}
							/>
						</span>
					</div>
					<div className={'header'}>
						<span>EMAIL</span>
						<span
							className={'header-arrows'}
							onClick={() => (handleArrowClick('EMAIL'))}
							style={{ cursor: "pointer" }}>
							<Icon
								name={ICONS.LITTLE_ARROW_UP}
								size={10}
								color={order === REGISTERED_USER_ORDER_OPTIONS.EMAIL_ASC ? COLORS.CYAN : COLORS.WHITE}
							/>
							<Icon
								name={ICONS.LITTLE_ARROW_DOWN}
								size={10}
								color={order === REGISTERED_USER_ORDER_OPTIONS.EMAIL_DESC ? COLORS.CYAN : COLORS.WHITE}
							/>
						</span>
					</div>
					<div className={'header'}>
						<span>LOCATION</span>
						<span
							className={'header-arrows'}
							onClick={() => (handleArrowClick('LOCATION'))}
							style={{ cursor: "pointer" }}>
							<Icon
								name={ICONS.LITTLE_ARROW_UP}
								size={10}
								color={order === REGISTERED_USER_ORDER_OPTIONS.LOCATION_ASC ? COLORS.CYAN : COLORS.WHITE}
							/>
							<Icon
								name={ICONS.LITTLE_ARROW_DOWN}
								size={10}
								color={order === REGISTERED_USER_ORDER_OPTIONS.LOCATION_DESC ? COLORS.CYAN : COLORS.WHITE}
							/>
						</span>
					</div>
					<div className={'header'}>
						<span>DATE</span>
						<span
							className={'header-arrows'}
							onClick={() => (handleArrowClick('DATE'))}
							style={{ cursor: "pointer" }}>
							<Icon
								name={ICONS.LITTLE_ARROW_UP}
								size={10}
								color={order === REGISTERED_USER_ORDER_OPTIONS.DATE_ASC ? COLORS.CYAN : COLORS.WHITE}
							/>
							<Icon
								name={ICONS.LITTLE_ARROW_DOWN}
								size={10}
								color={order === REGISTERED_USER_ORDER_OPTIONS.DATE_DESC ? COLORS.CYAN : COLORS.WHITE}
							/>
						</span>
					</div>

				</li>
				<>
					{registeredUsers && !loadingUsers ? (
						<>
							{count === 0 ?
								(searchValue === '' ?
									<div className={"people-loading"}>
										Your event currently has no registered attendees.
									</div>
									:
									<div className={"people-loading"}>
										No matches found for your search.
									</div>
								)
								: registeredUsers
							}
						</>
					) : (
						<div className={"people-loading"}>
							<WaitingIndicator />
						</div>
					)}
					{!loadingCount && count !== 0 && !loadingUsers ? (
						<li className="list-pagination">
							<Paging
								page={listPage}
								setPage={setListPage}
								totalPages={maxUserPage}
							/>
						</li>
					) : (
						!loadingUsers && count !== 0 &&
						<WaitingIndicator />
					)}
				</>
			</ul>
		</div>
	);
}